import React, { forwardRef, MouseEventHandler, PropsWithChildren } from 'react'
import clsx from 'clsx'
import { IconButton as IconButtonStyles } from '@airbus/elements/elements'
import { createUseStyles } from '../../lib/styling'
import useDeprecatedProps from '../../lib/hooks/useDeprecatedProps'
import { CommonProps } from '../helperTypes'

type DeprecatedSizes = 'extra-small' | 'extra-large'

type Props = PropsWithChildren<{
  /**
   * Pass true to disable the button
   * @default false
   */
  disabled?: boolean
  /** Button click handler */
  onClick?: MouseEventHandler<HTMLButtonElement>
  /** mouse down handler */
  onMouseDown?: MouseEventHandler<HTMLButtonElement>
  /** Toggle button and set aria-pressed accordingly */
  pressed?: boolean
  /**
   * The size of the button<br />
   * **Deprecated:** `extra-small` and `extra-large`
   * @default medium
   */
  size?: 'xsmall' | 'small' | 'medium' | 'large' | DeprecatedSizes
  /**
   * The variant of the button
   * @default secondary
   */
  variant?: 'primary' | 'secondary' | 'ghost' | 'success' | 'warning' | 'error'
}>

type DeprecatedProps = {
  color?: 'primary' | 'secondary' | 'default' | 'info' | 'warning' | 'error' | 'success' | 'white'
}

export type IconButtonProps = Props & DeprecatedProps & CommonProps

const getVariant = ({ variant = 'secondary', color }: IconButtonProps) => {
  // retro compatibility
  if (color && ['primary', 'secondary', 'warning', 'error', 'success'].indexOf(color) !== -1) return color
  if (color && color === 'info') return 'ghost'
  return variant
}

const getSize = ({ size = 'medium' }: IconButtonProps) => {
  if (size === 'extra-small') return 'small'
  if (size === 'extra-large') return 'medium'
  return size
}

const handleDeprecatedProps = (props: IconButtonProps) => {
  const { color, size, ...rest } = props
  return {
    variant: getVariant(props),
    size: getSize(props) || 'medium',
    ...rest,
  }
}

const useStyles = createUseStyles(IconButtonStyles, 'iconbutton')

export const IconButton = (props: IconButtonProps, ref: React.Ref<HTMLButtonElement>) => {
  const { variant, size, className, children, pressed, ...rest } = handleDeprecatedProps(props)
  const classes = useStyles()
  useDeprecatedProps('IconButton', { color: 'variant' }, props)

  return (
    <button
      type='button'
      ref={ref}
      className={clsx(className, classes.root, variant && classes[`_${variant}`], size && classes[`_${size}`])}
      aria-pressed={pressed}
      {...rest}
    >
      {children}
    </button>
  )
}

export default forwardRef<HTMLButtonElement, IconButtonProps>(IconButton)
